import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Container, createStyles, Grid, makeStyles, Paper, Theme, Typography } from '@material-ui/core'
import { observer } from 'mobx-react'
import { IMembershipUser, IMembershipUserBase } from '../../../../store/models/User/MembershipUser'
import { ITrack } from '../../../../store'
import { get } from '../../../../api/utils'
import { useParams } from 'react-router'
import DataTable, { IColData, ValueLabel } from '../../../shared/DataTableFilterable'
import LoadingAnimation from '../../../shared/LoadingAnimation'
import SummaryStats from '../../../admin/v2/partials/SummaryStats'

interface Props {
  membership: IMembershipUser
  track: ITrack
}

interface EngagementProps {
  details: {
    past_conversations?: string
    total_registered?: string
    total_attendees?: string
    upcoming_conversations?: string
    total_upcoming_registered?: string
    total_invites_sent?: string
  }
  details_by_conversation: {
    conversation?: string
    registrations?: number
    attendees?: number
    attendance?: number
  }[]
}

export const Engagement = observer(({ membership, track }: Props) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<EngagementProps>({ details: {}, details_by_conversation: [] })
  const { action, membership_id } = useParams<{ action: string; membership_id?: string }>()

  useEffect(() => {
    setLoading(true)
    get(`/memberships/${membership_id}/engagement`).then(({ json, response: { ok } }) => {
      if (ok) {
        setData(json)
      }
      setLoading(false)
    })
  }, [membership_id])

  if (loading) {
    return <LoadingAnimation />
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <div>
        <Typography variant='h6' className='reg-bottom reg-top'>
          Engagement Dashboard
        </Typography>
        <Grid container direction='column' alignItems='flex-start' spacing={3}>
          <Grid item className={classes.grid}>
            <EngagementDetail membership={membership} track={track} data={data} />
          </Grid>
        </Grid>
      </div>
      <div>
        <Typography variant='h6' className='reg-bottom reg-top'>
          Engagement Details by Conversation
        </Typography>
        <hr />
        <Grid container direction='column' alignItems='flex-start' spacing={3}>
          <Grid item className={classes.grid}>
            <EngagementDetailByConversation membership={membership} track={track} data={data} />
          </Grid>
        </Grid>
      </div>
    </div>
  )
})
export default Engagement

const AnalyticValue = ({ name, value }) => {
  const classes = useStyles()
  return (
    <Grid item xs={12} sm={4}>
      <Paper variant='outlined' className={classes.dataPaper}>
        <Typography variant='subtitle1'>{name}</Typography>
        <Typography variant='h5'>{value}</Typography>
      </Paper>
    </Grid>
  )
}

const EngagementDetail = ({ membership, track, data }: Props & { data: EngagementProps }) => {
  const classes = useStyles()
  return (
    <Container className={classes.section}>
      <div className='reg-left reg-bottom-padding'>
        <SummaryStats
          bottomDivider={false}
          data={[
            { title: 'Past Conversations', value: data.details.past_conversations },
            { title: 'Total Registered', value: data.details.total_registered },
            { title: 'Total Attendees', value: data.details.total_attendees }
          ]}
        />
        <hr className='reg-top reg-bottom' />
        <SummaryStats
          bottomDivider={false}
          data={[
            { title: 'Upcoming Conversations', value: data.details.upcoming_conversations },
            { title: 'Total Registered', value: data.details.total_upcoming_registered },
            { title: 'Total Invites Sent', value: data.details.total_invites_sent }
          ]}
        />
      </div>
    </Container>
  )
}

const EngagementDetailByConversation = ({ membership, track, data }: Props & { data: EngagementProps }) => {
  const classes = useStyles()
  const columnData: IColData[] = [
    { name: 'conversation', title: 'Conversation' },
    { name: 'registrations', title: 'Registrations', width: 130, align: 'right' },
    { name: 'attendees', title: 'Attendees', width: 130, align: 'right' },
    { name: 'attendance', title: 'Attendance', width: 130, align: 'right' }
  ]
  const totalReg = data.details_by_conversation.reduce((tot, val) => {
    return val.registrations ? tot + val.registrations : tot
  }, 0)
  const totalAtten = data.details_by_conversation.reduce((tot, val) => {
    return val.attendees ? tot + val.attendees : tot
  }, 0)
  return (
    <Container className={classes.section}>
      <Grid container justify='flex-end' alignItems='flex-end' alignContent='flex-end' className='reg-top'>
        <Grid item style={{ width: 'calc(100% - 450px)', textAlign: 'left' }}>
          Total
        </Grid>
        <Grid item style={{ width: 140, textAlign: 'right' }}>
          {totalReg}
        </Grid>
        <Grid item style={{ width: 140, textAlign: 'right' }}>
          {totalAtten}
        </Grid>
        <Grid item style={{ width: 140, textAlign: 'right' }}>
          {((totalAtten / totalReg) * 100.0).toFixed(1)}%
        </Grid>
      </Grid>
      {/* <div className='reg-left reg-bottom-padding'> */}
      <DataTable
        data={data.details_by_conversation}
        columns={columnData}
        loading={false}
        filterable={membership}
        rightColumns={['actions']}
        // height={'calc(100vh - 46.5em)'}
        // height={'20em'}
        noDataMsg='None'
        hidePaging
        hideSearch
        getCellValue={(row: any, columnName: string) => {
          const row_user = row.user
          switch (columnName) {
            case 'attendance':
              return <ValueLabel value={`${row.attendance.toFixed(1)}%`} />

            default:
              return <ValueLabel value={row[columnName]} />
          }
        }}
      />
      {/* </div> */}
    </Container>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      // backgroundColor: '#00ff00',
      width: '100%'
    },
    dataPaper: {
      padding: theme.spacing(2),
      borderRadius: '16px',
      maxWidth: '10rem'
      // minHeight: '6rem'
    },
    section: {
      // marginTop: '2rem',
      paddingTop: '.25rem'
      // minHeight: '10rem'
      // backgroundColor: '#ff0000',
      // width: '100%'
    }
  })
)
